import { ReferenceDirection, type ArdoqId } from '@ardoq/api-types';
import type {
  BubbleChartSourceOptions,
  BubbleChartValueSource,
  ComponentTypeRegistryValue,
  ComponentTypeSummary,
  FieldData,
  ReferenceTypeSummary,
} from '@ardoq/bubble-chart';
import { isTruthy } from '@ardoq/common-helpers';
import type { EnhancedScopeData, RepresentationData } from '@ardoq/data-model';
import { localeCompareNumericLowercase, type Locale } from '@ardoq/locale';
import type { PermissionContext } from '@ardoq/access-control';
import {
  colorsAndRepresentations,
  enhancedScopeDataOperations,
} from '@ardoq/scope-data';
import { groupBy, sumBy, uniq } from 'lodash';
import {
  getRelevantFieldInstances,
  type FieldInstance,
} from 'streams/fields/getFieldsForViewpoint';
import type { LinkedComponentWithReferenceTypeName } from '../referenceBubbleChart/viewModel$';
import type { ReferenceWithBubbleComponent } from '../referenceBubbleChart/types';
import {
  computeReferenceNumericFields,
  type ReferenceFields,
} from '../referenceBubbleChart/computeReferenceNumericFields';

const toComponentTypeRepresentationData = (
  enhancedScopeData: EnhancedScopeData,
  { componentType, components }: ComponentTypeRegistryValue
): RepresentationData => {
  const [componentId] = components;

  return colorsAndRepresentations.getComponentTypeRepresentationData(
    enhancedScopeData,
    componentType.id,
    enhancedScopeData.componentsById[componentId]?.model
  );
};

const countFieldNames = (
  fieldInstances: FieldInstance[],
  componentTypeIds: Set<ArdoqId>
): number =>
  new Set(
    getRelevantFieldInstances(fieldInstances, componentTypeIds).map(
      ({ field }) => field.name
    )
  ).size;

type ToComponentTypeSummariesArgs = {
  componentTypeRegistry: Map<ArdoqId, ComponentTypeRegistryValue>;
  enhancedScopeData: EnhancedScopeData;
  numericFieldInstances: FieldInstance[];
  locale: Locale;
};

const toComponentTypeSummaries = ({
  componentTypeRegistry,
  enhancedScopeData,
  numericFieldInstances,
  locale,
}: ToComponentTypeSummariesArgs): ComponentTypeSummary[] =>
  Object.values(
    groupBy(
      Array.from(componentTypeRegistry.values()),
      ({ componentType }) => componentType.name
    )
  )
    .map(registryValues => {
      const ids = registryValues.map(({ componentType }) => componentType.id);

      return {
        name: registryValues[0].componentType.name,
        ids,
        representationData: toComponentTypeRepresentationData(
          enhancedScopeData,
          registryValues[0]
        ),
        componentCount: sumBy(
          registryValues,
          ({ components }) => components.size
        ),
        numericFieldCount: countFieldNames(numericFieldInstances, new Set(ids)),
      };
    })
    .sort((a, b) => localeCompareNumericLowercase(a.name, b.name, locale));

const toLinkedComponentTypeName = (
  scopeData: EnhancedScopeData,
  { componentId }: LinkedComponentWithReferenceTypeName
): string | null =>
  enhancedScopeDataOperations.getComponentTypeByComponentId(
    scopeData,
    componentId
  )?.name ?? null;

const compareReferenceTypeSummaries =
  (locale: Locale) =>
  (a: ReferenceTypeSummary, b: ReferenceTypeSummary): number =>
    Number(a.numericFieldCount === 0) - Number(b.numericFieldCount === 0) ||
    localeCompareNumericLowercase(
      a.referenceTypeName,
      b.referenceTypeName,
      locale
    );

export type ReferenceTypeGroup = {
  referenceTypeName: string;
  direction: ReferenceDirection;
  references: ReferenceWithBubbleComponent[];
  referenceFields: ReferenceFields;
};

type ToReferenceTypeGroupsArgs = {
  incomingReferences: ReferenceWithBubbleComponent[];
  outgoingReferences: ReferenceWithBubbleComponent[];
  enhancedScopeData: EnhancedScopeData;
  permissionContext: PermissionContext;
};

export const toReferenceTypeGroups = ({
  incomingReferences,
  outgoingReferences,
  enhancedScopeData,
  permissionContext,
}: ToReferenceTypeGroupsArgs): ReferenceTypeGroup[] => {
  const referencesByDirection: [
    ReferenceDirection,
    ReferenceWithBubbleComponent[],
  ][] = [
    [ReferenceDirection.INCOMING, incomingReferences],
    [ReferenceDirection.OUTGOING, outgoingReferences],
  ];

  return referencesByDirection.flatMap(([direction, references]) =>
    Object.entries(groupBy(references, 'referenceTypeName')).map(
      ([referenceTypeName, referencesOfType]) => ({
        referenceTypeName,
        direction,
        references: referencesOfType,
        referenceFields: computeReferenceNumericFields({
          references: referencesOfType,
          permissionContext,
          scopeData: enhancedScopeData,
        }),
      })
    )
  );
};

export const findReferenceFields = (
  referenceTypeGroups: ReferenceTypeGroup[],
  valueSource: BubbleChartValueSource
): ReferenceFields | null => {
  if (valueSource.type !== 'reference') return null;
  return (
    referenceTypeGroups.find(
      ({ direction, referenceTypeName }) =>
        direction === valueSource.direction &&
        referenceTypeName === valueSource.referenceTypeName
    )?.referenceFields ?? null
  );
};

type ToReferenceTypeSummariesArgs = {
  referenceTypeGroups: ReferenceTypeGroup[];
  enhancedScopeData: EnhancedScopeData;
  locale: Locale;
};

const toReferenceTypeSummaries = ({
  referenceTypeGroups,
  enhancedScopeData,
  locale,
}: ToReferenceTypeSummariesArgs): ReferenceTypeSummary[] =>
  referenceTypeGroups
    .map(({ referenceTypeName, direction, references, referenceFields }) => ({
      referenceTypeName,
      direction,
      linkedComponentTypeNames: uniq(
        references
          .map(reference =>
            toLinkedComponentTypeName(enhancedScopeData, reference)
          )
          .filter(isTruthy)
      ).sort((a, b) => localeCompareNumericLowercase(a, b, locale)),
      numericFieldCount: referenceFields.numericFields.length,
    }))
    .sort(compareReferenceTypeSummaries(locale));

export type ToSourceOptionsArgs = ToComponentTypeSummariesArgs &
  ToReferenceTypeSummariesArgs & {
    allNumericFields: FieldData[];
  };

export const toSourceOptions = ({
  allNumericFields,
  componentTypeRegistry,
  referenceTypeGroups,
  enhancedScopeData,
  numericFieldInstances,
  locale,
}: ToSourceOptionsArgs): BubbleChartSourceOptions => ({
  allComponentsSummary: {
    componentCount: sumBy(
      Array.from(componentTypeRegistry.values()),
      ({ components }) => components.size
    ),
    numericFieldCount: allNumericFields.length,
  },
  componentTypeSummaries: toComponentTypeSummaries({
    componentTypeRegistry,
    enhancedScopeData,
    numericFieldInstances,
    locale,
  }),
  referenceTypeSummaries: toReferenceTypeSummaries({
    referenceTypeGroups,
    enhancedScopeData,
    locale,
  }),
});
